import { QbrActions, QbrActionTypes } from "@/Actions/QBR";
import { RiskMatrixCellData } from "@/Contracts/Assessments";
import {
	Qbr,
	HealthStatusVulnerabilitiesData
} from "@/Contracts/QBR";
import { Reducer } from "redux";

export interface QbrState {
	readonly isQbrLoading: boolean;
	readonly isQbrLogLoading: boolean;
	readonly qbrList: Qbr[];
	readonly currentActiveQbr: Qbr | null;
	readonly manualQbrDefaults: HealthStatusVulnerabilitiesData[];
	readonly qbrRiskMatrixData: RiskMatrixCellData[] | null;
	readonly qbrRiskBarScore: number | null;
	readonly healthStatusData: HealthStatusVulnerabilitiesData[] | null;
}

export const initialQbrState: QbrState = {
	isQbrLoading: false,
	isQbrLogLoading: false,
	qbrList: new Array<Qbr>(),
	currentActiveQbr: null,
	manualQbrDefaults: new Array<HealthStatusVulnerabilitiesData>(),
	qbrRiskMatrixData: null,
	qbrRiskBarScore: null,
	healthStatusData: null
};

export const QbrReducer: Reducer<QbrState, QbrActions> = (
	state: QbrState = initialQbrState,
	action: QbrActions
): QbrState => {
	switch (action.type) {
		case QbrActionTypes.SHOW_QBR_LOADER: {
			return {
				...state,
				isQbrLoading: true
			};
		}
		case QbrActionTypes.HIDE_QBR_LOADER: {
			return {
				...state,
				isQbrLoading: false
			};
		}
		case QbrActionTypes.SHOW_QBR_LOG_LOADER: {
			return {
				...state,
				isQbrLogLoading: true
			};
		}
		case QbrActionTypes.HIDE_QBR_LOG_LOADER: {
			return {
				...state,
				isQbrLogLoading: false
			};
		}
		case QbrActionTypes.QBR_DETAILS_REQUESTED: {
			return state;
		}
		case QbrActionTypes.QBR_DETAILS_SUCCESSFUL: {
			return {
				...state,
				currentActiveQbr: action.currentActiveQbr,
				qbrRiskMatrixData:
					action.currentActiveQbr.risk_matrix_cell_data || null,
				qbrRiskBarScore: action.currentActiveQbr.risk_bar_score || null,
				healthStatusData:
					action.currentActiveQbr.health_status_data || null
			};
		}
		case QbrActionTypes.QBR_DETAILS_FAILED: {
			return state;
		}
		case QbrActionTypes.EDIT_QBR_REQUESTED: {
			return state;
		}
		case QbrActionTypes.EDIT_QBR_SUCCESSFUL: {
			return {
				...state,
				currentActiveQbr: action.currentActiveQbr,
				qbrRiskMatrixData:
					action.currentActiveQbr.risk_matrix_cell_data || null,
				qbrRiskBarScore: action.currentActiveQbr.risk_bar_score || null,
				healthStatusData:
					action.currentActiveQbr.health_status_data || null
			};
		}
		case QbrActionTypes.EDIT_QBR_FAILED: {
			return state;
		}
		case QbrActionTypes.QBR_RESET: {
			return {
				...state,
				qbrList: new Array<Qbr>()
			};
		}
		case QbrActionTypes.QBR_DETAILS_RESET: {
			return {
				...state,
				currentActiveQbr: null,
				qbrRiskBarScore: null,
				qbrRiskMatrixData: null,
				healthStatusData: null
			};
		}
		case QbrActionTypes.ADD_QBR_DATA_LOG_REQUESTED: {
			return state;
		}
		case QbrActionTypes.ADD_QBR_DATA_LOG_SUCCESSFUL: {
			if (state.currentActiveQbr) {
				const existingLogs =
					state.currentActiveQbr.category_qbr_logs.data;
				const newLogs = [action.addQbrLog, ...existingLogs];
				return {
					...state,
					currentActiveQbr: {
						...state.currentActiveQbr,
						category_qbr_logs: { data: newLogs },
						risk_matrix_cell_data:
							action.addQbrLog.risk_matrix_cell_data,
						risk_bar_score: action.addQbrLog.risk_bar_score
					},
					qbrRiskMatrixData:
						action.addQbrLog.risk_matrix_cell_data || null,
					qbrRiskBarScore: action.addQbrLog.risk_bar_score || null,
					healthStatusData:
						action.addQbrLog.health_status_data || null
				};
			} else {
				return state;
			}
		}
		case QbrActionTypes.ADD_QBR_DATA_LOG_FAILED: {
			return state;
		}
		case QbrActionTypes.EDIT_QBR_DATA_LOG_REQUESTED: {
			return state;
		}
		case QbrActionTypes.EDIT_QBR_DATA_LOG_SUCCESSFUL: {
			if (state.currentActiveQbr) {
				const existingLogs =
					state.currentActiveQbr.category_qbr_logs.data;
				const newLogs = existingLogs.map(eL =>
					eL.id === action.editQbrLog.id ? action.editQbrLog : eL
				);
				return {
					...state,
					currentActiveQbr: {
						...state.currentActiveQbr,
						category_qbr_logs: { data: newLogs }
					},
					qbrRiskMatrixData:
						action.editQbrLog.risk_matrix_cell_data || null,
					qbrRiskBarScore: action.editQbrLog.risk_bar_score || null,
					healthStatusData:
						action.editQbrLog.health_status_data || null
				};
			} else {
				return state;
			}
		}
		case QbrActionTypes.EDIT_QBR_DATA_LOG_FAILED: {
			return state;
		}
		case QbrActionTypes.DELETE_QBR_DATA_LOG_SUCCESSFUL: {
			if (state.currentActiveQbr) {
				const existingLogs =
					state.currentActiveQbr.category_qbr_logs.data;
				const newLogs = existingLogs.filter(
					log => log.id !== action.deleteQbrLog.id
				);
				return {
					...state,
					currentActiveQbr: {
						...state.currentActiveQbr,
						category_qbr_logs: { data: newLogs }
					}
				};
			} else {
				return state;
			}
		}
		case QbrActionTypes.CREATE_QBR_CATEGORY_REQUESTED: {
			return state;
		}
		case QbrActionTypes.CREATE_QBR_CATEGORY_FAILED: {
			return state;
		}
		case QbrActionTypes.CREATE_QBR_CATEGORY_SUCCESSFUL: {
			if (state.currentActiveQbr) {
				return {
					...state,
					currentActiveQbr: {
						...state.currentActiveQbr,
						health_status_data_list: [
							...action.currentActiveQbr.health_status_data_list
						]
					}
				};
			} else {
				return state;
			}
		}
		case QbrActionTypes.UPDATE_QBR_CATEGORY_REQUESTED: {
			return state;
		}
		case QbrActionTypes.UPDATE_QBR_CATEGORY_SUCCESS: {
			if (state.currentActiveQbr) {
				return {
					...state,
					currentActiveQbr: {
						...state.currentActiveQbr,
						health_status_data_list: [
							...action.currentActiveQbr.health_status_data_list
						],
						category_qbr_logs: {
							data: [
								...action.currentActiveQbr.category_qbr_logs
									.data
							]
						}
					},
					healthStatusData:
						action.currentActiveQbr.health_status_data || null
				};
			} else {
				return state;
			}
		}
		case QbrActionTypes.UPDATE_QBR_CATEGORY_FAILED: {
			return state;
		}
		case QbrActionTypes.DELETE_QBR_CATEGORY_REQUESTED: {
			return state;
		}
		case QbrActionTypes.DELETE_QBR_CATEGORY_SUCCESS: {
			if (state.currentActiveQbr) {
				return {
					...state,
					currentActiveQbr: {
						...state.currentActiveQbr,
						health_status_data_list: [
							...action.currentActiveQbr.health_status_data_list
						],
						category_qbr_logs: {
							data: [
								...(action.currentActiveQbr.category_qbr_logs
									?.data ?? [])
							]
						}
					},
					healthStatusData:
						action.currentActiveQbr.health_status_data ?? null
				};
			} else {
				return state;
			}
		}
		case QbrActionTypes.DELETE_QBR_CATEGORY_FAILED: {
			return state;
		}
		case QbrActionTypes.REORDER_QBR_CATEGORIES_FAILED: {
			return state;
		}
		case QbrActionTypes.REORDER_QBR_CATEGORIES_REQUESTED: {
			return state;
		}
		case QbrActionTypes.REORDER_QBR_CATEGORIES_SUCCESS: {
			if (state.currentActiveQbr) {
				return {
					...state,
					currentActiveQbr: {
						...state.currentActiveQbr,
						health_status_data_list: [
							...action.currentActiveQbr.health_status_data_list
						]
					},
					healthStatusData:
						action.currentActiveQbr.health_status_data ?? null
				};
			} else {
				return state;
			}
		}
		default:
			return state;
	}
};

export default QbrReducer;
